import { useState, useEffect, useRef } from "react";
import { X, Save, Scissors, BookMarked, FileDown, Route, Copy, Upload, Download } from "lucide-react";
import { toast } from "sonner";
import api, { API, openDownload } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { COLOR_HEX } from "@/lib/modules";
import { useI18n } from "@/context/I18nContext";

function OtdrChart({ trace, events }) {
  if (!trace || trace.length < 2) return <div className="text-slate-500 text-sm p-6 text-center">Brak danych trasy w pliku .sor</div>;
  const W = 760, H = 260, pad = 36;
  const xs = trace.map((p) => p[0]), ys = trace.map((p) => p[1]);
  const xmin = Math.min(...xs), xmax = Math.max(...xs), ymin = Math.min(...ys), ymax = Math.max(...ys);
  const xr = (xmax - xmin) || 1, yr = (ymax - ymin) || 1;
  const sx = (x) => pad + (x - xmin) / xr * (W - pad - 12);
  const sy = (y) => H - pad - (y - ymin) / yr * (H - pad - 12);
  const dpath = trace.map((p, i) => `${i ? "L" : "M"}${sx(p[0]).toFixed(1)},${sy(p[1]).toFixed(1)}`).join(" ");
  const evLines = (events || []).map((ev, i) => {
    const ex = parseFloat(String(ev.distance).replace(",", "."));
    if (isNaN(ex) || ex < xmin || ex > xmax) return null;
    const x = sx(ex);
    return (<g key={i}><line x1={x} y1={12} x2={x} y2={H - pad} stroke="#f59e0b" strokeWidth="0.8" strokeDasharray="3 2" /><text x={x + 2} y={22} fill="#fbbf24" fontSize="9">{ex.toFixed(2)}</text></g>);
  });
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" data-testid="otdr-chart">
      <rect x="0" y="0" width={W} height={H} fill="#0b1428" stroke="#334155" />
      <line x1={pad} y1={H - pad} x2={W - 12} y2={H - pad} stroke="#475569" />
      <line x1={pad} y1={12} x2={pad} y2={H - pad} stroke="#475569" />
      <path d={dpath} fill="none" stroke="#06b6d4" strokeWidth="1" />
      {evLines}
      <text x={pad} y={H - 8} fill="#94a3b8" fontSize="11">{xmin.toFixed(2)} km</text>
      <text x={W - 70} y={H - 8} fill="#94a3b8" fontSize="11">{xmax.toFixed(2)} km</text>
      <text x="4" y="18" fill="#94a3b8" fontSize="11">{ymax.toFixed(1)} dB</text>
      <text x="4" y={H - pad} fill="#94a3b8" fontSize="11">{ymin.toFixed(1)} dB</text>
    </svg>
  );
}

export default function FibersPanel({ cable, onClose, onTraceFiber }) {
  const { t } = useI18n();
  const { settings } = useSettings();
  const otdrStatus = (m) => {
    if (!m) return null;
    if (m.total_loss_db == null) return "unknown";
    const max = Number(settings.otdr_max_loss_db) || 5;
    return m.total_loss_db <= max ? "ok" : "fail";
  };
  const [fibers, setFibers] = useState([]);
  const [otdr, setOtdr] = useState({});
  const [otdrView, setOtdrView] = useState(null);
  const [hoverFiber, setHoverFiber] = useState(null);
  const [flashFiber, setFlashFiber] = useState(null);
  const rowRefs = useRef({});
  const scrollToFiber = (num) => {
    const el = rowRefs.current[num];
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    setFlashFiber(num);
    setTimeout(() => setFlashFiber((v) => (String(v) === String(num) ? null : v)), 1500);
  };
  const [bulkResult, setBulkResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const cut = new Set((cable.cutFibers || []).map(Number));
  const fromMufa = !!cable.closureNo;

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cable.id]);

  const load = () => {
    setLoading(true);
    api.get(`/cables/${cable.id}/fibers`).then((r) => setFibers(r.data)).finally(() => setLoading(false));
    api.get(`/cables/${cable.id}/otdr`).then((r) => {
      const map = {}; (r.data || []).forEach((m) => { map[m.fiber] = m; }); setOtdr(map);
    }).catch(() => {});
  };

  const uploadOtdr = async (fiber, fileObj) => {
    if (!fileObj) return;
    const fd = new FormData(); fd.append("file", fileObj);
    try {
      const { data } = await api.post(`/cables/${cable.id}/fibers/${fiber}/otdr`, fd);
      setOtdr((p) => ({ ...p, [fiber]: data }));
      toast.success(data.parse_error ? t("Plik OTDR zapisany (nie udało się odczytać metryk)") : t("Dodano pomiar OTDR"));
    } catch { toast.error(t("Błąd wgrywania pomiaru OTDR")); }
  };
  const downloadOtdr = (m) => {
    openDownload(`/otdr/${m.id}/download`);
  };
  const deleteOtdr = async (m, fiber) => {
    if (!window.confirm(t("Usunąć pomiar OTDR tego włókna?"))) return;
    try { await api.delete(`/otdr/${m.id}`); setOtdr((p) => { const n = { ...p }; delete n[fiber]; return n; }); toast.success(t("Usunięto pomiar OTDR")); }
    catch { toast.error(t("Błąd usuwania")); }
  };
  const uploadBulk = async (fileList) => {
    if (!fileList || !fileList.length) return;
    const fd = new FormData();
    Array.from(fileList).forEach((f) => fd.append("files", f));
    try {
      const { data } = await api.post(`/cables/${cable.id}/otdr-bulk`, fd);
      toast.success(`${t("Zaimportowano pomiary OTDR")}: ${data.assigned}/${data.total}`);
      setBulkResult(data);
      load();
    } catch { toast.error(t("Błąd masowego importu OTDR")); }
  };

  const reserve = async (num) => {
    const who = window.prompt(t("Zarezerwowano dla (klient/projekt)"));
    if (who === null) return;
    try {
      await api.post("/reservations", { resource_type: "fiber", resource_id: cable.id, detail: String(num), reserved_for: who });
      toast.success(t("Rezerwacja utworzona"));
      load();
    } catch { toast.error(t("Błąd zapisu")); }
  };

  const upd = (i, k, v) => setFibers((p) => p.map((f, idx) => idx === i ? { ...f, [k]: v } : f));

  const save = async () => {
    try {
      await api.put(`/cables/${cable.id}/fibers`, fibers);
      toast.success(t("Zapisano włókna"));
      onClose();
    } catch { toast.error(t("Błąd zapisu")); }
  };

  const exportPdf = async () => {
    try {
      const res = await api.get(`/cables/${cable.id}/fibers-passport`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `paszport_wlokien_${cable.number}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error(t("Nie udało się wygenerować PDF")); }
  };

  const copyLabel = async (f) => {
    const stLbl = f.reserved ? `${t("Rezerwacja")}${f.reserved_for ? ` (${f.reserved_for})` : ""}` : t(f.status);
    const parts = [`${t("Kabel")} ${cable.number}`, `wł. ${f.number}`, f.color, stLbl];
    if (f.leads_to) parts.push(f.leads_to);
    try { await navigator.clipboard.writeText(parts.join(" · ")); toast.success(t("Skopiowano etykietę")); }
    catch { toast.error(t("Nie udało się skopiować")); }
  };

  const isOccupied = (f) => f.status === "zajęte" || f.status === "zajete" || f.status === "uszkodzone";
  const stKind = (f) => (f.reserved ? "rez" : (isOccupied(f) ? "zaj" : "wol"));
  const ST = { zaj: { c: "#38bdf8", l: "Zajęte" }, wol: { c: "#22c55e", l: "Wolne" }, rez: { c: "#f59e0b", l: "Rezerwacja" } };
  const cRez = fibers.filter((f) => f.reserved).length;
  const cZaj = fibers.filter((f) => isOccupied(f) && !f.reserved).length;
  const cWol = fibers.length - cZaj - cRez;

  const tubes = [];
  const tmap = {};
  fibers.forEach((f) => { const k = f.tube || "T"; if (!tmap[k]) { tmap[k] = { tube: k, color: f.tube_color, fibers: [] }; tubes.push(tmap[k]); } tmap[k].fibers.push(f); });
  const N = Math.max(tubes.length, 1);
  const CX = 230, CY = 230;
  const ringR = N > 1 ? 132 : 0;
  const tr = N <= 6 ? 52 : (N <= 9 ? 44 : 38);
  const hoverOtdr = hoverFiber != null ? otdr[hoverFiber] : null;

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/70 p-4" data-testid="fibers-panel">
      <div className="bg-[#0b1220] border border-slate-700/70 rounded-2xl w-full max-w-6xl max-h-[92vh] flex flex-col shadow-2xl">
        <div className="flex items-center justify-between px-6 pt-4 pb-2">
          <div>
            <div className="text-xs text-slate-500">{t("Kable")} / {cable.number} / {t("Włókna")}</div>
            <h3 className="font-heading font-bold text-2xl text-white mt-0.5">{t("Kabel")} {cable.number} · {fibers.length}J
              {fromMufa && <span className="text-slate-400 font-normal text-base"> · {t("mufa")} {cable.closureNo}</span>}
            </h3>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="fibers-close"><X size={22} /></button>
        </div>
        <div className="grid md:grid-cols-[300px_1fr] gap-4 px-6 pb-4 overflow-hidden flex-1 min-h-0">
          <div className="rounded-2xl bg-[#0e1830] border border-slate-800 flex flex-col p-4">
            <div className="flex-1 min-h-0 flex items-center justify-center">
            {loading ? <div className="text-slate-500 text-sm">{t("Ładowanie…")}</div> : (
              <svg viewBox="0 0 460 460" className="w-full h-auto max-h-[38vh] max-w-[280px]">
                <circle cx={CX} cy={CY} r={214} fill="none" stroke="#1e293b" strokeWidth="10" />
                <circle cx={CX} cy={CY} r={200} fill="#0b1428" stroke="#334155" strokeWidth="2" />
                {tubes.map((tb, ti) => {
                  const a = -Math.PI / 2 + ti * 2 * Math.PI / N;
                  const tx = CX + ringR * Math.cos(a), ty = CY + ringR * Math.sin(a);
                  const thex = COLOR_HEX[tb.color] || "#64748b";
                  const hlTube = tb.fibers.some((f) => String(f.number) === String(hoverFiber));
                  return (
                    <g key={tb.tube}>
                      {hlTube && <circle cx={tx} cy={ty} r={tr + 5} fill="none" stroke="#ffffff" strokeWidth="2" opacity="0.85" className="fp-fiber-blink" />}
                      <circle cx={tx} cy={ty} r={tr} fill="#0a1122" stroke={thex} strokeWidth={hlTube ? 8 : 5} opacity={hlTube ? 1 : 0.85} />
                      {tb.fibers.map((f, fi) => {
                        const m = tb.fibers.length;
                        const fa = -Math.PI / 2 + fi * 2 * Math.PI / Math.max(m, 1);
                        const inr = m > 1 ? tr * 0.56 : 0;
                        const fx = tx + inr * Math.cos(fa), fy = ty + inr * Math.sin(fa);
                        const hlF = String(f.number) === String(hoverFiber);
                        return <circle key={f.number} cx={fx} cy={fy} r={hlF ? (tr > 44 ? 9 : 7.5) : (tr > 44 ? 5 : 4.2)} fill={COLOR_HEX[f.color] || "#94a3b8"} stroke={hlF ? "#ffffff" : "#0a1122"} strokeWidth={hlF ? 1.8 : 0.8} className={hlF ? "fp-fiber-blink" : ""} style={{ cursor: "pointer" }} data-testid={`diagram-fiber-${f.number}`} onMouseEnter={() => setHoverFiber(f.number)} onMouseLeave={() => setHoverFiber(null)} onClick={() => scrollToFiber(f.number)} />;
                      })}
                      <text x={CX + (ringR + tr + 12) * Math.cos(a)} y={CY + (ringR + tr + 12) * Math.sin(a) + 4} fill={hlTube ? "#ffffff" : "#94a3b8"} fontSize={hlTube ? 15 : 13} fontWeight={hlTube ? "bold" : "normal"} textAnchor="middle">{tb.tube}</text>
                    </g>
                  );
                })}
              </svg>
            )}
            </div>
            {hoverOtdr && (hoverOtdr.trace || []).length > 1 ? (
              <div className="mt-2" data-testid="otdr-mini-preview">
                <div className="text-[11px] text-slate-300 mb-1 flex items-center justify-between">
                  <span className="font-semibold">{t("wł.")} {hoverFiber} · {t("reflektogram")}</span>
                  <span className="font-mono text-slate-400">{hoverOtdr.total_length_m != null ? `${hoverOtdr.total_length_m} m` : ""}{hoverOtdr.total_loss_db != null ? ` · ${hoverOtdr.total_loss_db} dB` : ""}</span>
                </div>
                <OtdrChart trace={hoverOtdr.trace} events={hoverOtdr.events} />
              </div>
            ) : (
              <div className="mt-3 self-start bg-cyan-500/10 border-l-4 border-cyan-400 rounded-r-lg px-4 py-2 backdrop-blur" data-testid="fibers-diagram-caption">
                <div className="text-lg font-heading font-bold text-slate-100">{t("Paszportyzacja włókien")}</div>
              </div>
            )}
          </div>
          <div className="flex flex-col min-h-0">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-lg font-heading font-bold text-white">{t("Paszport włókien")}</h4>
              <div className="flex items-center gap-2">
                <span className="flex items-center gap-1.5 text-xs bg-slate-800/70 border border-slate-700 rounded-full px-2.5 py-1"><span className="w-2 h-2 rounded-full" style={{ background: ST.zaj.c }} />{t("Zajęte")} <b className="text-white">{cZaj}</b></span>
                <span className="flex items-center gap-1.5 text-xs bg-slate-800/70 border border-slate-700 rounded-full px-2.5 py-1"><span className="w-2 h-2 rounded-full" style={{ background: ST.wol.c }} />{t("Wolne")} <b className="text-white">{cWol}</b></span>
                <span className="flex items-center gap-1.5 text-xs bg-slate-800/70 border border-slate-700 rounded-full px-2.5 py-1"><span className="w-2 h-2 rounded-full" style={{ background: ST.rez.c }} />{t("Rezerwacje")} <b className="text-white">{cRez}</b></span>
              </div>
            </div>
            <div className="overflow-auto rounded-xl border border-slate-800 flex-1 min-h-0" data-testid="fibers-table-wrap">
              <table className="w-full min-w-[600px] text-sm">
                <thead className="text-[11px] uppercase tracking-wide text-slate-500 bg-[#0e1830] sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium">{t("Nr")}</th>
                    <th className="text-left px-2 py-2 font-medium">{t("Kolor")}</th>
                    <th className="text-left px-2 py-2 font-medium">{t("Status")}</th>
                    <th className="text-left px-2 py-2 font-medium">{t("Połączenie")}</th>
                    <th className="text-left px-2 py-2 font-medium">OTDR</th>
                    <th className="text-left px-2 py-2 font-medium">{t("Rezerwacja")}</th>
                  </tr>
                </thead>
                <tbody>
                  {fibers.map((f, i) => {
                    const k = stKind(f);
                    const isCut = cut.has(Number(f.number));
                    return (
                      <tr key={f.number} ref={(el) => { if (el) rowRefs.current[f.number] = el; }} onMouseEnter={() => setHoverFiber(f.number)} onMouseLeave={() => setHoverFiber(null)} className={`border-t border-slate-800/70 hover:bg-slate-800/40 transition-colors ${String(f.number) === String(flashFiber) ? "bg-cyan-500/20 ring-1 ring-cyan-400" : ""}`} data-testid={`fiber-row-${f.number}`}>
                        <td className="px-3 py-2 text-slate-300">
                          <div className="flex items-center gap-1.5">
                            <button onClick={() => onTraceFiber && onTraceFiber(cable, f.number)} data-testid={`fiber-trace-${f.number}`}
                              title={t("Pokaż trasę włókna na mapie")} className="inline-flex items-center gap-1.5 hover:text-cyan-300">
                              <Route size={12} className="text-slate-500" /> {f.number}
                            </button>
                            <button onClick={() => copyLabel(f)} data-testid={`fiber-copy-${f.number}`}
                              title={t("Kopiuj etykietę włókna")} className="text-slate-500 hover:text-cyan-300"><Copy size={11} /></button>
                          </div>
                        </td>
                        <td className="px-2 py-2">
                          <span className="inline-flex items-center gap-2">
                            <span className="w-4 h-4 rounded-sm border border-slate-600" style={{ background: COLOR_HEX[f.color] || "#94a3b8" }} />
                            <span className="text-slate-200">{f.color}</span>
                          </span>
                        </td>
                        <td className="px-2 py-2">
                          <span className="inline-flex items-center gap-1.5">
                            <span className="w-2 h-2 rounded-full" style={{ background: ST[k].c }} />
                            <select value={f.status} onChange={(e) => upd(i, "status", e.target.value)} data-testid={`fiber-status-${f.number}`}
                              className="bg-transparent border-0 text-slate-200 text-sm focus:ring-0 cursor-pointer">
                              {["wolne", "zajęte", "uszkodzone", "rezerwowe"].map((s) => <option key={s} value={s} className="bg-slate-800">{t(s)}</option>)}
                            </select>
                            {isCut && <Scissors size={12} className="text-orange-300" title={t("przecięte")} />}
                          </span>
                        </td>
                        <td className="px-2 py-2">
                          <input value={f.leads_to || ""} onChange={(e) => upd(i, "leads_to", e.target.value)} placeholder="—"
                            className="bg-transparent border border-transparent hover:border-slate-700 focus:border-slate-600 rounded px-1.5 py-0.5 text-sm text-slate-300 w-full" />
                        </td>
                        <td className="px-2 py-2">
                          {otdr[f.number] ? (
                            <div className="flex items-center gap-1.5 text-xs">
                              <button onClick={() => setOtdrView(otdr[f.number])} data-testid={`otdr-view-${f.number}`} title={t("Podgląd reflektogramu")}
                                className={`whitespace-nowrap hover:underline inline-flex items-center gap-1 ${otdrStatus(otdr[f.number]) === "ok" ? "text-green-400" : otdrStatus(otdr[f.number]) === "fail" ? "text-red-400" : "text-amber-400"}`}>
                                <span className={`w-1.5 h-1.5 rounded-full ${otdrStatus(otdr[f.number]) === "ok" ? "bg-green-400" : otdrStatus(otdr[f.number]) === "fail" ? "bg-red-400" : "bg-amber-400"}`}></span>
                                {otdr[f.number].total_length_m != null ? `${otdr[f.number].total_length_m} m` : t("plik")}
                                {otdr[f.number].total_loss_db != null ? ` · ${otdr[f.number].total_loss_db} dB` : ""}
                              </button>
                              <button onClick={() => downloadOtdr(otdr[f.number])} data-testid={`otdr-download-${f.number}`} title={t("Pobierz .sor")} className="text-slate-400 hover:text-cyan-300"><Download size={12} /></button>
                              <button onClick={() => deleteOtdr(otdr[f.number], f.number)} data-testid={`otdr-delete-${f.number}`} title={t("Usuń pomiar OTDR")} className="text-slate-500 hover:text-red-400"><X size={12} /></button>
                            </div>
                          ) : (
                            <label data-testid={`otdr-upload-${f.number}`} title={t("Wgraj pomiar OTDR (.sor)")} className="text-slate-500 hover:text-cyan-300 cursor-pointer inline-flex items-center gap-1 text-xs">
                              <Upload size={12} /> .sor
                              <input type="file" accept=".sor" className="hidden" onChange={(e) => { uploadOtdr(f.number, e.target.files?.[0]); e.target.value = ""; }} />
                            </label>
                          )}
                        </td>
                        <td className="px-2 py-2 text-slate-400">
                          {f.reserved ? (
                            <span className="text-amber-300" title={f.reserved_for}>{f.reserved_for || t("rezerwacja")}</span>
                          ) : (
                            <button onClick={() => reserve(f.number)} data-testid={`fiber-reserve-${f.number}`} className="text-slate-500 hover:text-amber-300 inline-flex items-center gap-1" title={t("Zarezerwuj włókno")}>
                              — <BookMarked size={12} />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="px-6 py-3 border-t border-slate-800 flex justify-end gap-2">
          <label data-testid="otdr-bulk-import" title={t("Masowy import .sor — numer włókna z nazwy pliku")}
            className="px-4 py-2 text-sm rounded-lg bg-slate-700 hover:bg-slate-600 text-white flex items-center gap-2 cursor-pointer">
            <Upload size={15} /> {t("Import .sor")}
            <input type="file" accept=".sor" multiple className="hidden" onChange={(e) => { uploadBulk(e.target.files); e.target.value = ""; }} />
          </label>
          <button onClick={exportPdf} data-testid="fibers-export-pdf" className="px-4 py-2 text-sm rounded-lg bg-slate-700 hover:bg-slate-600 text-white flex items-center gap-2">
            <FileDown size={15} /> {t("Eksport PDF")}
          </button>
          <button onClick={save} data-testid="fibers-save" className="px-4 py-2 text-sm rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white flex items-center gap-2">
            <Save size={15} /> {t("Zapisz włókna")}
          </button>
        </div>
      </div>
      {otdrView && (
        <div className="absolute inset-0 z-[10] flex items-center justify-center bg-black/80 p-6" data-testid="otdr-preview" onClick={() => setOtdrView(null)}>
          <div className="bg-[#0b1220] border border-slate-700 rounded-2xl w-full max-w-4xl max-h-[88vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-800">
              <div>
                <div className="text-xs text-slate-500">{t("Reflektogram OTDR")}</div>
                <div className="text-base font-heading font-bold text-white">{cable.number} · {t("wł.")} {otdrView.fiber}
                  <span className="text-slate-400 font-normal text-sm"> · {otdrView.original_filename}</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => downloadOtdr(otdrView)} data-testid="otdr-preview-download" className="text-slate-300 hover:text-cyan-300" title={t("Pobierz .sor")}><Download size={18} /></button>
                <button onClick={() => setOtdrView(null)} data-testid="otdr-preview-close" className="text-slate-400 hover:text-white"><X size={20} /></button>
              </div>
            </div>
            <div className="p-5 overflow-y-auto">
              <div className="grid grid-cols-4 gap-2 mb-4 text-center">
                <div className="bg-slate-800/60 rounded-lg py-2"><div className="text-sm font-mono text-white">{otdrView.wavelength || "—"}</div><div className="text-[10px] text-slate-500 uppercase">{t("Fala")}</div></div>
                <div className="bg-slate-800/60 rounded-lg py-2"><div className="text-sm font-mono text-white">{otdrView.total_length_m != null ? `${otdrView.total_length_m} m` : "—"}</div><div className="text-[10px] text-slate-500 uppercase">{t("Długość")}</div></div>
                <div className="bg-slate-800/60 rounded-lg py-2"><div className="text-sm font-mono text-orange-300">{otdrView.total_loss_db != null ? `${otdrView.total_loss_db} dB` : "—"}</div><div className="text-[10px] text-slate-500 uppercase">{t("Tłumienie")}</div></div>
                <div className="bg-slate-800/60 rounded-lg py-2"><div className="text-sm font-mono text-white">{otdrView.num_events != null ? otdrView.num_events : "—"}</div><div className="text-[10px] text-slate-500 uppercase">{t("Zdarzenia")}</div></div>
              </div>
              {otdrView.parse_error && <div className="text-xs text-amber-400 mb-3">{t("Nie udało się odczytać metryk z pliku")} — {otdrView.parse_error}</div>}
              <div className="grid grid-cols-4 gap-2 mb-3 text-center">
                <div className="bg-slate-800/40 rounded-lg py-1.5"><div className="text-xs font-mono text-slate-200">{otdrView.range_m != null ? `${otdrView.range_m} m` : "—"}</div><div className="text-[10px] text-slate-500 uppercase">{t("Zakres")}</div></div>
                <div className="bg-slate-800/40 rounded-lg py-1.5"><div className="text-xs font-mono text-slate-200">{otdrView.pulse_width || "—"}</div><div className="text-[10px] text-slate-500 uppercase">{t("Impuls")}</div></div>
                <div className="bg-slate-800/40 rounded-lg py-1.5"><div className="text-xs font-mono text-slate-200">{otdrView.ior || "—"}</div><div className="text-[10px] text-slate-500 uppercase">{t("Wsp. załamania")}</div></div>
                <div className="bg-slate-800/40 rounded-lg py-1.5"><div className="text-xs font-mono text-slate-200">{otdrView.fiber_type || "—"}</div><div className="text-[10px] text-slate-500 uppercase">{t("Typ włókna")}</div></div>
              </div>
              <OtdrChart trace={otdrView.trace} events={otdrView.events} />
              {(otdrView.events || []).length > 0 && (
                <div className="mt-4 rounded-lg border border-slate-800 overflow-hidden">
                  <table className="w-full text-sm" data-testid="otdr-events-table">
                    <thead className="text-[11px] uppercase tracking-wide text-slate-500 bg-[#0e1830]">
                      <tr><th className="text-left px-3 py-2">#</th><th className="text-left px-3 py-2">{t("Typ")}</th><th className="text-left px-3 py-2">{t("Odległość")}</th><th className="text-left px-3 py-2">{t("Tłumienie")}</th><th className="text-left px-3 py-2">{t("Odbicie")}</th></tr>
                    </thead>
                    <tbody>
                      {otdrView.events.map((ev) => (
                        <tr key={ev.n} className="border-t border-slate-800/70 text-slate-300">
                          <td className="px-3 py-1.5">{ev.n}</td><td className="px-3 py-1.5">{ev.type || "—"}</td>
                          <td className="px-3 py-1.5">{ev.distance || "—"}</td><td className="px-3 py-1.5">{ev.splice_loss || "—"}</td><td className="px-3 py-1.5">{ev.refl_loss || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {bulkResult && (
        <div className="absolute inset-0 z-[11] flex items-center justify-center bg-black/80 p-6" data-testid="otdr-bulk-result" onClick={() => setBulkResult(null)}>
          <div className="bg-[#0b1220] border border-slate-700 rounded-2xl w-full max-w-5xl max-h-[88vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-800">
              <div className="text-base font-heading font-bold text-white">{t("Import OTDR")} — {bulkResult.assigned}/{bulkResult.total} {t("przypisano")}</div>
              <button onClick={() => setBulkResult(null)} data-testid="otdr-bulk-close" className="text-slate-400 hover:text-white"><X size={20} /></button>
            </div>
            <div className="p-5 overflow-y-auto">
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                {bulkResult.results.filter((r) => r.ok).map((r, i) => (
                  <button key={i} onClick={() => setOtdrView({ ...r, original_filename: r.filename })} data-testid={`otdr-thumb-${r.fiber}`}
                    className="text-left bg-slate-800/50 hover:bg-slate-800 border border-slate-700 hover:border-cyan-600 rounded-lg p-2 transition-colors">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-white">{t("wł.")} {r.fiber}</span>
                      <span className="text-[11px] text-slate-400">{r.total_length_m != null ? `${r.total_length_m} m` : ""}{r.total_loss_db != null ? ` · ${r.total_loss_db} dB` : ""}</span>
                    </div>
                    <div className="pointer-events-none"><OtdrChart trace={r.trace} events={r.events} /></div>
                    <div className="text-[10px] text-slate-500 truncate mt-1">{r.filename}</div>
                  </button>
                ))}
              </div>
              {bulkResult.results.some((r) => !r.ok) && (
                <div className="mt-4 text-sm">
                  <div className="text-amber-400 font-semibold mb-1">{t("Nieprzypisane pliki")}:</div>
                  <ul className="text-slate-400 text-xs list-disc pl-5">
                    {bulkResult.results.filter((r) => !r.ok).map((r, i) => (<li key={i}>{r.filename} — {r.error}</li>))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
